import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';
import ReclamationFormModal from './ReclamationFormModal';
import ReclamationDetailModal from './ReclamationDetailModal';
import './Reclamations.css';

const API_URL = import.meta.env.VITE_API_URL;

const Reclamations = ({ onNavigate, onLogout, currentPage }) => {
  const { hasPermission } = usePermissions();
  const getToken = () => localStorage.getItem('token');

  const [reclamations, setReclamations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistiques, setStatistiques] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    waiting: 0,
    critical: 0,
    resolved: 0,
    closed: 0
  });

  // Filtres & Recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('all');
  const [filtrePriorite, setFiltrePriorite] = useState('all');
  const [filtreCategorie, setFiltreCategorie] = useState('all');
  const [filtreAssigne, setFiltreAssigne] = useState('all');
  const [onlyMine, setOnlyMine] = useState(false);
  const [sortBy, setSortBy] = useState('newest');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [reclamationToEdit, setReclamationToEdit] = useState(null);
  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchReclamations();
    fetchStatistiques();
  }, [filtreStatut, filtrePriorite, filtreCategorie, filtreAssigne, onlyMine, sortBy]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setUsersList(list.filter(u => ['admin', 'moderateur', 'super_admin'].includes((u.typeUser || '').toLowerCase())));
    } catch (_) {}
  };

  const fetchReclamations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtreStatut !== 'all') params.append('statut', filtreStatut);
      if (filtrePriorite !== 'all') params.append('priorite', filtrePriorite);
      if (filtreCategorie !== 'all') params.append('categorie', filtreCategorie);
      if (filtreAssigne !== 'all') params.append('assignedTo', filtreAssigne);
      if (onlyMine) params.append('mesReclamations', 'true');
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (sortBy) params.append('sort', sortBy);

      const response = await axios.get(`${API_URL}/reclamations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setReclamations(response.data.reclamations || response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des réclamations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistiques = async () => {
    try {
      const response = await axios.get(`${API_URL}/reclamations/statistiques/all`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStatistiques(response.data);
    } catch (error) {
      console.error('Erreur statistiques:', error);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchReclamations();
  };

  const handleViewDetail = async (rec) => {
    try {
      // Charger la réclamation complète avec history et comments populating
      const res = await axios.get(`${API_URL}/reclamations/${rec._id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedReclamation(res.data);
      setShowDetailModal(true);
    } catch (_) {
      setSelectedReclamation(rec);
      setShowDetailModal(true);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPriorityBadge = (priorite) => {
    const map = {
      low: { label: '🟢 Faible', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
      medium: { label: '🟡 Normale', bg: '#fefce8', color: '#854d0e', border: '#fef08a' },
      high: { label: '🟠 Élevée', bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' },
      critical: { label: '🔥 CRITIQUE', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }
    };
    const p = map[priorite] || map.medium;
    return (
      <span style={{ padding: '3px 8px', borderRadius: '10px', background: p.bg, color: p.color, border: `1px solid ${p.border}`, fontWeight: 700, fontSize: '0.78rem' }}>
        {p.label}
      </span>
    );
  };

  const getStatusBadge = (statut) => {
    const map = {
      open: { label: 'Ouverte', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
      en_attente: { label: 'Ouverte', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
      in_progress: { label: 'En cours', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
      en_cours: { label: 'En cours', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
      waiting: { label: 'En attente', bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
      resolved: { label: 'Résolue', bg: '#dcfce7', color: '#166534', border: '#86efac' },
      résolue: { label: 'Résolue', bg: '#dcfce7', color: '#166534', border: '#86efac' },
      closed: { label: 'Clôturée', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
      fermée: { label: 'Clôturée', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' }
    };
    const s = map[statut] || map.open;
    return (
      <span style={{ padding: '3px 8px', borderRadius: '10px', background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 700, fontSize: '0.78rem' }}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="rec-container">
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />

      <div className="rec-content">
        {/* HEADER PAGE */}
        <div className="rec-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.6rem', fontWeight: 800 }}>⚡ Gestion des Réclamations</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.92rem' }}>Suivi centralisé, traçabilité Audit Trail et résolution des problèmes clients.</p>
          </div>
          {hasPermission('reclamations', 'create') && (
            <button
              onClick={() => {
                setReclamationToEdit(null);
                setShowCreateModal(true);
              }}
              style={{ background: '#1e3a5f', color: '#ffffff', border: 'none', padding: '12px 22px', borderRadius: '10px', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 4px 10px rgba(30,58,95,0.25)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <span>➕</span> Nouvelle réclamation
            </button>
          )}
        </div>

        {/* GRILLE DE STATISTIQUES */}
        <div className="rec-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '20px', marginBottom: '24px' }}>
          <div className="rec-stat-card rec-stat-total" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700 }}>Total</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e3a5f', marginTop: '4px' }}>{statistiques.total || 0}</div>
          </div>
          <div className="rec-stat-card" style={{ background: '#ffffff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 700 }}>Ouvertes</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1d4ed8', marginTop: '4px' }}>{statistiques.open || statistiques.enAttente || 0}</div>
          </div>
          <div className="rec-stat-card" style={{ background: '#ffffff', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: 700 }}>En cours</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#15803d', marginTop: '4px' }}>{statistiques.inProgress || statistiques.enCours || 0}</div>
          </div>
          <div className="rec-stat-card" style={{ background: '#ffffff', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 700 }}>En attente</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#b45309', marginTop: '4px' }}>{statistiques.waiting || 0}</div>
          </div>
          <div className="rec-stat-card" style={{ background: '#ffffff', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 700 }}>🔥 Critiques</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#dc2626', marginTop: '4px' }}>{statistiques.critical || 0}</div>
          </div>
          <div className="rec-stat-card" style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '0.8rem', color: '#166534', fontWeight: 700 }}>Résolues</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#166534', marginTop: '4px' }}>{statistiques.resolved || statistiques.resolues || 0}</div>
          </div>
        </div>

        {/* BARRE DE FILTRES ET RECHERCHE */}
        <div style={{ background: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="🔍 Rechercher par Référence (ex: REC-2026), Client, Téléphone, CIN ou Sujet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem' }}
            />
            <button type="submit" style={{ padding: '10px 20px', background: '#1e3a5f', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>
              Rechercher
            </button>
          </form>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '2px' }}>Statut</label>
              <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}>
                <option value="all">Tous les statuts</option>
                <option value="open">Ouverte</option>
                <option value="in_progress">En cours</option>
                <option value="waiting">En attente</option>
                <option value="resolved">Résolue</option>
                <option value="closed">Clôturée</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '2px' }}>Priorité</label>
              <select value={filtrePriorite} onChange={(e) => setFiltrePriorite(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}>
                <option value="all">Toutes priorités</option>
                <option value="low">🟢 Faible</option>
                <option value="medium">🟡 Normale</option>
                <option value="high">🟠 Élevée</option>
                <option value="critical">🔥 CRITIQUE</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '2px' }}>Catégorie</label>
              <select value={filtreCategorie} onChange={(e) => setFiltreCategorie(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}>
                <option value="all">Toutes catégories</option>
                <option value="Dossier">Dossier</option>
                <option value="Paiement">Paiement</option>
                <option value="STEG">STEG</option>
                <option value="Installation">Installation</option>
                <option value="Matériel">Matériel</option>
                <option value="Facturation">Facturation</option>
                <option value="Livraison">Livraison</option>
                <option value="SAV">SAV</option>
                <option value="Commercial">Commercial</option>
                <option value="Administratif">Administratif</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '2px' }}>Assigné à</label>
              <select value={filtreAssigne} onChange={(e) => setFiltreAssigne(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}>
                <option value="all">Tous les utilisateurs</option>
                {usersList.map(u => (
                  <option key={u._id} value={u._id}>{u.prenom} {u.nom}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px', gap: '6px' }}>
              <input
                type="checkbox"
                id="onlyMineCheck"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <label htmlFor="onlyMineCheck" style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e3a5f', cursor: 'pointer' }}>
                👤 Mes Réclamations uniquement
              </label>
            </div>
          </div>
        </div>

        {/* TABLEAU DES RÉCLAMATIONS */}
        {loading ? (
          <LoadingSpinner message="Chargement des réclamations clients..." />
        ) : reclamations.length === 0 ? (
          <div className="rec-empty-state" style={{ background: '#ffffff', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
            <div className="rec-empty-icon" style={{ fontSize: '2.5rem', marginBottom: '10px' }}>📂</div>
            <h3 style={{ margin: 0, color: '#334155' }}>Aucune réclamation trouvée</h3>
            <p style={{ margin: '6px 0 16px', color: '#64748b', fontSize: '0.9rem' }}>Modifiez vos critères de recherche ou ajoutez une nouvelle réclamation.</p>
            {hasPermission('reclamations', 'create') && (
              <button
                onClick={() => {
                  setReclamationToEdit(null);
                  setShowCreateModal(true);
                }}
                style={{ background: '#1e3a5f', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
              >
                + Créer une réclamation
              </button>
            )}
          </div>
        ) : (
          <div className="rec-table-container" style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
            <table className="rec-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textTransform: 'uppercase', fontSize: '0.78rem' }}>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>Référence</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>Client</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>Sujet / Catégorie</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center' }}>Priorité</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center' }}>Statut</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>Assigné à</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reclamations.map((rec) => {
                  const clientLabel = rec.client
                    ? `${rec.client.prenom || ''} ${rec.client.nom || ''}`.trim()
                    : 'Client inconnu';

                  return (
                    <tr key={rec._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 800, color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                        {rec.reference || 'REC-2026'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{clientLabel}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>📞 {rec.client?.telephone || 'N/A'}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{rec.sujet}</div>
                        <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600 }}>
                          {rec.categorie || 'Autre'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        {getPriorityBadge(rec.priorite)}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        {getStatusBadge(rec.statut)}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#475569' }}>
                        {rec.assignedTo ? `${rec.assignedTo.prenom || ''} ${rec.assignedTo.nom || ''}` : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Non assigné</span>}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatDate(rec.createdAt)}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleViewDetail(rec)}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e3a5f', fontWeight: 700, cursor: 'pointer' }}
                            title="Voir détails & Audit Trail"
                          >
                            👁️
                          </button>
                          {hasPermission('reclamations', 'update') && (
                            <button
                              type="button"
                              onClick={() => {
                                setReclamationToEdit(rec);
                                setShowCreateModal(true);
                              }}
                              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#d97706', fontWeight: 700, cursor: 'pointer' }}
                              title="Modifier"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* MODAL FORMULAIRE CRÉATION / ÉDITION */}
        {showCreateModal && (
          <ReclamationFormModal
            reclamationToEdit={reclamationToEdit}
            onClose={() => {
              setShowCreateModal(false);
              setReclamationToEdit(null);
            }}
            onSuccess={() => {
              fetchReclamations();
              fetchStatistiques();
            }}
          />
        )}

        {/* MODAL DÉTAILS AVEC AUDIT TRAIL */}
        {showDetailModal && selectedReclamation && (
          <ReclamationDetailModal
            reclamation={selectedReclamation}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedReclamation(null);
            }}
            onRefresh={() => {
              fetchReclamations();
              fetchStatistiques();
            }}
            onNavigate={onNavigate}
            onEdit={(rec) => {
              setReclamationToEdit(rec);
              setShowCreateModal(true);
            }}
            hasPermission={hasPermission}
          />
        )}
      </div>
    </div>
  );
};

export default Reclamations;
